import parse from "html-react-parser";

const ASPECT_BUCKETS = [
  { label: "16:9", ratio: 16 / 9 },
  { label: "4:5", ratio: 4 / 5 },
  { label: "1:1", ratio: 1 },
  { label: "4:3", ratio: 4 / 3 },
];

const DEFAULT_BUCKET = ASPECT_BUCKETS.find(({ label }) => label === "4:5")!;

const toNumber = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const pickPrimaryMedia = (contentUrls: any[] = []) => {
  const list = Array.isArray(contentUrls) ? contentUrls : [];
  const priority = ["video", "image"];

  for (const type of priority) {
    const match = list.find(
      (entry: any) => entry?.type?.toLowerCase() === type
    );
    if (match) return match;
  }

  return list[0];
};

const computeRatio = (media: any) => {
  const width = toNumber(media?.width);
  const height = toNumber(media?.height);
  const factor = toNumber(media?.factor);

  if (width && height) {
    const r = width / height;
    if (Number.isFinite(r) && r > 0) return r;
  }

  if (factor) return factor;

  return DEFAULT_BUCKET.ratio;
};

const pickNearestBucket = (ratio: number) => {
  const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 0;

  return ASPECT_BUCKETS.reduce((closest, bucket) => {
    const delta = Math.abs(safeRatio - bucket.ratio);
    const closestDelta = Math.abs(safeRatio - closest.ratio);
    return delta < closestDelta ? bucket : closest;
  }, DEFAULT_BUCKET);
};

const hasKnownVideoExtension = (value: string, extension: string) => {
  const normalized = value.split("#")[0].split("?")[0].toLowerCase();
  return normalized.endsWith(extension);
};

const filterPlayableUrls = (urls: any[] = []) => {
  const allowedExts = [".mp4", ".m3u8"];
  return urls.filter((u: string) =>
    allowedExts.some(
      (ext) => typeof u === "string" && hasKnownVideoExtension(u, ext)
    )
  );
};

const sanitizeContentUrls = (contentUrls: any[] = []) => {
  const list = Array.isArray(contentUrls) ? contentUrls : [];
  return list
    .map((entry: any) => {
      const type = (entry?.type || "").toLowerCase();
      // Only sanitize video entries; leave images/others untouched
      if (type === "video") {
        const cleanedUrls = filterPlayableUrls(entry?.urls || []);
        return cleanedUrls.length
          ? {
              ...entry,
              urls: cleanedUrls,
            }
          : null;
      }
      return entry;
    })
    .filter(Boolean);
};

const normalizeTemplateType = (value: any) => {
  const normalized = String(value || "").toUpperCase();
  return normalized === "POST" ? "POST" : "VERTICAL_VIDEO";
};

const resolveTemplateType = (video: any) => {
  const metakeys = video?.metakeys ?? {};
  const candidates = [
    video?.templateType,
    metakeys?.templateType,
    video?.template_type,
    metakeys?.template_type,
  ];
  const templateValue = candidates.find(
    (item) => typeof item === "string" && item.length > 0
  );
  return normalizeTemplateType(templateValue);
};

export const getVideosData = (videos: any, len: any = 0) => {
  try {
    const safeVideos = Array.isArray(videos) ? videos : [];

    return safeVideos.map((video: any, index: number) => {
      const metakeys = video?.metakeys ?? {};
      const mergedVideo = { ...video, ...metakeys };
      const templateType = resolveTemplateType(video);

      const sanitizedContentUrls = sanitizeContentUrls(
        mergedVideo?.contentUrls || video?.contentUrls
      );
      const primaryMedia = pickPrimaryMedia(sanitizedContentUrls);
      const ratio =
        toNumber(mergedVideo?.aspectRatio) ||
        toNumber(mergedVideo?.aspectBucket?.ratio) ||
        computeRatio(primaryMedia);
      const aspectBucket = pickNearestBucket(ratio);
      const mediaUrlsRaw = Array.isArray(primaryMedia?.urls)
        ? primaryMedia.urls
        : [];
      const mediaUrls = filterPlayableUrls(mediaUrlsRaw);
      const hlsVideoUrl =
        mediaUrls.find(
          (mediaUrl: string) =>
            typeof mediaUrl === "string" &&
            hasKnownVideoExtension(mediaUrl, ".m3u8")
        ) || "";
      const [smallVideoUrl = "", largeVideoUrl = ""] = mediaUrls;
      const fallbackUrl =
        sanitizedContentUrls?.[0]?.urls?.[0] ?? "";
      const playbackUrl = hlsVideoUrl || smallVideoUrl || fallbackUrl;

      const isYoutubeVideo =
        smallVideoUrl.includes("youtube") || largeVideoUrl.includes("youtube");

      let youtubeVideoId = "";
      if (isYoutubeVideo) {
        const splittedUrl = smallVideoUrl.split("/");
        youtubeVideoId = splittedUrl[splittedUrl.length - 1];
      }

      const indexNo = len + index;

      return {
        ...video,
        ...metakeys,
        templateType,
        primaryMedia,
        aspectBucket,
        aspectRatio: aspectBucket?.ratio ?? DEFAULT_BUCKET.ratio,
        contentUrls: sanitizedContentUrls,
        indexNo,
        id: `${mergedVideo?.videoId || mergedVideo?.id || index}-${indexNo}`,
        playing: false,
        url: playbackUrl,
        thumbnailUrl: video?.thumbnailUrl,
        isYoutubeVideo,
        youtubeVideoId: isYoutubeVideo ? youtubeVideoId : "",
        likeEnabled: mergedVideo?.likeEnabled ?? true,
        shareEnabled: mergedVideo?.shareEnabled ?? true,
        commentEnabled: mergedVideo?.commentEnabled ?? true,
      };
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};

const optimisticUnlocks = new Map<string, Set<string>>();

export const getOptimisticUnlockIds = (seriesId?: string) => {
  if (!seriesId) return [];
  const key = String(seriesId);
  const set = optimisticUnlocks.get(key);
  return set ? Array.from(set) : [];
};

export const addOptimisticUnlockId = (
  seriesId?: string,
  episodeId?: string,
) => {
  if (!seriesId || !episodeId) return;
  const key = String(seriesId);
  const id = String(episodeId);
  let set = optimisticUnlocks.get(key);
  if (!set) {
    set = new Set<string>();
    optimisticUnlocks.set(key, set);
  }
  set.add(id);
};

export const parseDescription = (description: string, taggedUsers: any) => {
  const regex = /(#[^\s]+)|(@[^\s]+)/g;
  let hasTags = false;
  const descriptionWithAnchorTags = description?.replace(regex, (match) => {
    if (match.startsWith("#")) {
      hasTags = true;
      return `<a href="/hashtag/${match.slice(1)}">${match}</a>`;
    } else if (match.startsWith("@")) {
      hasTags = true;
      //   const taggedUser = taggedUsers?.find((user:any)=> user?.userName?.replace(" ","")?.replace("@","") === match?.replace(" ","")?.replace("@",""));
      const normalizedMatch = match.replace(/[@\s]/g, "");
      const taggedUser = taggedUsers?.find(
        (user: any) =>
          (user?.userName ?? "").replace(/[@\s]/g, "") === normalizedMatch
      );
      return `<a href="/profile/${taggedUser?.userId}">${match}</a>`;
    } else {
      return match;
    }
  });

  if (hasTags) {
    return parse(descriptionWithAnchorTags);
  }

  return description;
};

export const convertKToNumeric = (kValue: any) => {
  if (typeof kValue === "string" && kValue.match(/(\d+(\.\d+)?)(K|M|B)?/)) {
    let match: any = kValue.match(/(\d+(\.\d+)?)(K|M|B)?/);
    let numericValue = parseFloat(match[1]);
    let modifier = match[3];
    if (modifier) {
      if (modifier === "K") {
        numericValue *= 1000;
      } else if (modifier === "M") {
        numericValue *= 1000000;
      } else if (modifier === "B") {
        numericValue *= 1000000000;
      }
    }
    return numericValue;
  }
  return null;
};

export const formatLargeNumber = (count: number) => {
  if (count < 1000) {
    return count.toString();
  } else if (count < 1000000) {
    return (count / 1000).toFixed(1) + "K";
  } else {
    return (count / 1000000).toFixed(1) + "M";
  }
};
